/** angular */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
/** end angular */

/** services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
/** end services */

/** models */
import { DsCampaign } from '../models/ds-campaign.class';
/** end models */

/** functions */
import { BydGenerateParams } from '../../../functions/byd-generate-params.function';
/** end functions */

/** interfaces */
import { DsCampaignInterface } from '../interfaces/ds-campaign.interface';
import { Campaigns } from '../../ds-pins/ds-pins-index';
import { DsCampaignsFilterInterface } from '../interfaces/ds-campaigns-filter.interface';
/** end interfaces */
@Injectable({
  providedIn: 'root',
})
export class DsCampaignsService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Get list of campaigns.
   *
   * @param projectId Current project id
   * @param elementsPerPage Number of elements per page. Defaults to 10
   * @param pageIndex Requested page index. Starts at 0
   * @param filters Optional filters
   * @returns Observable<DsCampaign[]>
   */
  public getCampaigns(
    projectId: number,
    elementsPerPage = 10,
    pageIndex = 0,
    filters?: DsCampaignsFilterInterface
  ): Observable<DsCampaign[]> {
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(elementsPerPage, pageIndex);
    const params = BydGenerateParams<DsCampaignsFilterInterface>(filters);
    this.path = `/v1/projects/${projectId}/campaigns`;
    return this.get<DsCampaign[]>({ headers, params });
  }

  /**
   * Get old campaigns list, containing unneeded keys and not implementing pagination.
   *
   * @param projectjId the project id
   * @returns Observable<Campaigns[]>
   */
  public getAllCampaigns(projectjId: number): Observable<Campaigns[]> {
    this.path = `/projects/${projectjId}/campaigns`;
    return this.get();
  }

  /**
   * Add an campaign.
   *
   * @param projectId the project id
   * @param title the campaign title
   * @returns Observable<number>
   */
  public addCampaign(projectId: number, title: string): Observable<number> {
    this.path = `/projects/${projectId}/campaigns`;
    return this.post<number>({ body: { title } });
  }

  /**
   * Get number of pins attached to the campaign.
   *
   * @param prjId Project id
   * @param campaignId Campaign id
   * @returns Observable<number>
   */
  public getCampaignPinCount(prjId: number, campaignId: number): Observable<number> {
    this.path = `/projects/${prjId}/campaigns/${campaignId}/count`;
    return this.get<number>();
  }

  /**
   * Returns all campaigns for a given company id.
   *
   * @param projectId Project id
   * @param companyId The selected company id
   * @returns Observable<DsCampaign[]>
   */
  public getCompanyCampaigns(projectId: number, companyId: number): Observable<DsCampaign[]> {
    this.path = `/v1/projects/${projectId}/campaigns/companies/${companyId}`;
    return this.get<DsCampaign[]>();
  }

  /**
   * Rename chosen campaign.
   *
   * @param projectId Project id
   * @param campaignId Campaign id
   * @param title New title of campaign
   * @returns Observable<boolean>
   */
  public renameCampaign(projectId: number, campaignId: number, title: string): Observable<boolean> {
    this.path = `/projects/${projectId}/campaigns/${campaignId}`;
    return this.patch<boolean>({ body: { title } });
  }

  /**
   * Delete an campaign.
   *
   * @param projectId the project id
   * @param campaignId the campaign id
   * @returns Observable<boolean>
   */
  public deleteCampaign(projectId: number, campaignId: number): Observable<boolean> {
    this.path = `/projects/${projectId}/campaigns/${campaignId}`;
    return this.delete<boolean>();
  }

  /**
   * Create DsCampaign instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `DsCampaign` instance.
   */
  public createNewInstance(jsonObject: DsCampaignInterface): DsCampaign {
    return new DsCampaign(jsonObject);
  }
}
